import { useState, useRef, useCallback, useEffect } from 'react';
import { Plus, Check, X, Loader2, Bold, Italic, AlertTriangle, Brain } from 'lucide-react';
import api from '../../services/api';
import useCreditPacks from '../../hooks/useCreditPacks';
import AiQuotaPill from '../ui/AiQuotaPill';
import BuyPackButton from '../custom/BuyPackButton';

/**
 * RichInput — simple contentEditable with bold/italic toolbar
 */
function RichInput({ value, onChange, placeholder, disabled, rows = 2 }) {
    const ref = useRef(null);

    const execCmd = (cmd) => {
        document.execCommand(cmd, false, null);
        if (ref.current) onChange(ref.current.innerHTML);
    };

    const handleInput = () => {
        if (ref.current) onChange(ref.current.innerHTML);
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', gap: 2, marginBottom: 4 }}>
                <button type="button" className="qc-toolbar-btn" onClick={() => execCmd('bold')} title="Gras" disabled={disabled}>
                    <Bold size={13} />
                </button>
                <button type="button" className="qc-toolbar-btn" onClick={() => execCmd('italic')} title="Italique" disabled={disabled}>
                    <Italic size={13} />
                </button>
            </div>
            <div
                ref={ref}
                className="qc-rich-input"
                contentEditable={!disabled}
                onInput={handleInput}
                onPaste={handlePaste}
                dangerouslySetInnerHTML={{ __html: value }}
                style={{ minHeight: rows * 24 }}
                data-placeholder={placeholder}
            />
        </div>
    );
}

/**
 * QuestionCreator — inline form to add a manual question.
 *
 * Props:
 *  - sousSujetId: int
 *  - onCreated: () => void (fired after successful insert)
 *  - onClose: () => void
 *  - currentCount: int (current question count for the subject)
 *  - autoGenerateAi: bool — if true, trigger IA generation immediately on mount
 */
export default function QuestionCreator({ sousSujetId, thematiqueId, onCreated, onClose, currentCount, autoGenerateAi = false }) {
    // Quota "Questions IA" (plafond par thématique + crédits Pack IA), affiché DANS
    // le formulaire à côté du bouton de génération. Hook appelé inconditionnellement.
    const { monoCap, monoUsed, monoRemaining } = useCreditPacks(thematiqueId);
    const baseRemaining = Math.max(0, (monoCap ?? 0) - monoUsed);
    const hasQuota = thematiqueId != null && monoCap != null;
    const aiQuotaPill = hasQuota ? {
        used: Math.min(monoUsed, monoCap), limit: monoCap, remaining: baseRemaining,
        exceeded: monoUsed >= monoCap && monoRemaining <= 0, enabled: true,
    } : null;
    const aiExceeded = hasQuota && (baseRemaining + monoRemaining) <= 0;

    const [enonce, setEnonce] = useState('');
    const [reponses, setReponses] = useState([
        { id: 'a', content: '' },
        { id: 'b', content: '' },
        { id: 'c', content: '' },
        { id: 'd', content: '' },
    ]);
    const [bonneReponse, setBonneReponse] = useState('a');
    const [explication, setExplication] = useState('');
    const [saving, setSaving] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiNotice, setAiNotice] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const updateReponse = (id, content) => {
        setReponses(prev => prev.map(r => r.id === id ? { ...r, content } : r));
    };

    const handleGenerateWithAi = useCallback(async () => {
        setAiLoading(true);
        setError('');
        setAiNotice('');
        try {
            const { data } = await api.post('/custom/generate-question.php', {
                sous_sujet_id: sousSujetId,
                preview_only: true,
            });
            const q = data.data?.question;
            if (q) {
                setEnonce(q.enonce_text || '');
                const map = q.reponses_text || {};
                setReponses([
                    { id: 'a', content: map.a || '' },
                    { id: 'b', content: map.b || '' },
                    { id: 'c', content: map.c || '' },
                    { id: 'd', content: map.d || '' },
                ]);
                setBonneReponse(q.bonne_reponse || 'a');
                setExplication(q.explication_text || '');
                setAiNotice(data.data?.fallback_used
                    ? 'Proposition générée (bascule temporaire en mode payant). Relisez avant de valider.'
                    : 'Proposition générée. Relisez et ajustez avant de valider.');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur de génération IA');
        }
        setAiLoading(false);
    }, [sousSujetId]);

    // Auto-trigger IA on mount when requested
    useEffect(() => {
        if (autoGenerateAi && sousSujetId) {
            handleGenerateWithAi();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        setError('');
        setSuccess('');

        const cleanEnonce = enonce.replace(/<[^>]*>/g, '').trim();
        if (!cleanEnonce) return setError('L\'énoncé est requis');

        const filledReponses = reponses.filter(r => r.content.trim());
        if (filledReponses.length < 2) return setError('Au moins 2 réponses sont requises');

        if (!filledReponses.find(r => r.id === bonneReponse)) {
            return setError('La bonne réponse doit être parmi les réponses remplies');
        }

        setSaving(true);
        try {
            const { data } = await api.post('/custom/create-question.php', {
                sous_sujet_id: sousSujetId,
                enonce,
                reponses: filledReponses,
                bonne_reponse: bonneReponse,
                explication: explication || '',
            });
            if (data.success) {
                setSuccess(`Question ajoutée (${data.data.count}/50)`);
                // Reset form for next question
                setEnonce('');
                setReponses([
                    { id: 'a', content: '' },
                    { id: 'b', content: '' },
                    { id: 'c', content: '' },
                    { id: 'd', content: '' },
                ]);
                setBonneReponse('a');
                setExplication('');
                onCreated?.();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setSaving(false);
    };

    return (
        <div className="qc-creator" onClick={e => e.stopPropagation()}>
            <div className="qc-creator__header">
                <strong><Plus size={14} /> Ajouter une question</strong>
                <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>{currentCount}/50</span>
                <button type="button" className="qc-creator__close" onClick={onClose}><X size={14} /></button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="qc-ai-bar">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={handleGenerateWithAi}
                        disabled={aiLoading || saving || currentCount >= 50 || aiExceeded}
                        title={aiExceeded
                            ? `Quota questions IA atteint (${monoUsed}/${monoCap}) — achetez un Pack IA.`
                            : "Pré-remplir le formulaire avec une question générée par l'IA"}
                    >
                        {aiLoading
                            ? <><Loader2 size={13} className="spin" /> Génération...</>
                            : <><Brain size={13} /> {enonce ? 'Régénérer avec l\'IA' : 'Générer avec l\'IA'}</>
                        }
                    </button>
                    {aiQuotaPill && <AiQuotaPill quota={aiQuotaPill} label="Questions IA" />}
                    {hasQuota && monoRemaining > 0 && (
                        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }} title="Crédits Pack IA actifs">
                            +{monoRemaining} pack
                        </span>
                    )}
                    {thematiqueId != null && <BuyPackButton thematiqueId={thematiqueId} compact />}
                    {!aiQuotaPill && (
                        <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                            Relisez et ajustez avant de valider
                        </span>
                    )}
                </div>
                {aiNotice && <div className="qc-success" style={{ background: 'rgba(139,92,246,0.08)', borderColor: '#8B5CF6', color: '#8B5CF6' }}><Brain size={13} /> {aiNotice}</div>}

                <div className="qc-field">
                    <label className="qc-label">&Eacute;nonc&eacute;</label>
                    <RichInput value={enonce} onChange={setEnonce} placeholder="Votre question..." disabled={saving || aiLoading} rows={2} />
                </div>

                <div className="qc-field">
                    <label className="qc-label">R&eacute;ponses <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(cochez la bonne)</span></label>
                    {reponses.map(r => (
                        <div key={r.id} className="qc-reponse-row">
                            <label className="qc-radio">
                                <input type="radio" name="bonne_reponse" value={r.id}
                                    checked={bonneReponse === r.id}
                                    onChange={() => setBonneReponse(r.id)} />
                                <span className={`qc-radio__letter ${bonneReponse === r.id ? 'qc-radio__letter--active' : ''}`}>
                                    {r.id.toUpperCase()}
                                </span>
                            </label>
                            <input type="text" className="qc-reponse-input"
                                value={r.content}
                                onChange={e => updateReponse(r.id, e.target.value)}
                                placeholder={`Réponse ${r.id.toUpperCase()}`}
                                disabled={saving || aiLoading} />
                        </div>
                    ))}
                </div>

                <div className="qc-field">
                    <label className="qc-label">Explication <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optionnel)</span></label>
                    <RichInput value={explication} onChange={setExplication} placeholder="Pourquoi cette réponse est correcte..." disabled={saving || aiLoading} rows={2} />
                </div>

                {error && <div className="qc-error"><AlertTriangle size={13} /> {error}</div>}
                {success && <div className="qc-success"><Check size={13} /> {success}</div>}

                <div className="qc-actions">
                    <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Fermer</button>
                    <button type="submit" className="btn btn-sm btn-primary" disabled={saving || currentCount >= 50}>
                        {saving ? <><Loader2 size={13} className="spin" /> Ajout...</> : <><Check size={13} /> Valider et ajouter</>}
                    </button>
                </div>
            </form>
        </div>
    );
}
